import React, { useEffect, useMemo, useState } from 'react';
import { UsersIcon, CalendarIcon, SparklesIcon } from 'lucide-react';
import SearchBar from '../../components/Events/SearchBar';
import EventCard from '../../components/Events/EventCard';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { EVENT_CATEGORIES, SampleEvent } from '../../data/eventCategoryData';
interface CategoryFilter {
  label: string;
  slugs: string[] | 'all';
}
const CATEGORY_FILTERS: CategoryFilter[] = [{
  label: 'All',
  slugs: 'all'
}, {
  label: 'Meetups',
  slugs: ['reading-meetups', 'book-swaps', 'silent-book-clubs']
}, {
  label: 'Book Clubs',
  slugs: ['book-clubs-in-person', 'book-clubs-virtual']
}, {
  label: 'Virtual',
  slugs: ['virtual-author-events', 'livestream-discussions', 'book-clubs-virtual']
}, {
  label: 'Signings',
  slugs: ['book-signings', 'multi-author-signings', 'local-authors']
}, {
  label: 'Workshops',
  slugs: ['writing-workshops', 'publishing-workshops', 'craft-masterclasses', 'online-workshops']
}, {
  label: 'Launches',
  slugs: ['book-launches', 'midnight-releases']
}];
const isFreeEvent = (event: SampleEvent): boolean => event.price === 'Free' || event.isFree === true || !event.ticketPrice && !event.price;
const FreeEvents = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [locationSearch, setLocationSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [savedEvents, setSavedEvents] = useState<number[]>([]);
  useEffect(() => {
    const saved = localStorage.getItem('savedEvents');
    if (saved) {
      try {
        setSavedEvents(JSON.parse(saved));
      } catch {
        // ignore
      }
    }
  }, []);
  useEffect(() => {
    localStorage.setItem('savedEvents', JSON.stringify(savedEvents));
  }, [savedEvents]);
  const handleToggleSave = (eventId: number) => {
    setSavedEvents(prev => prev.includes(eventId) ? prev.filter(id => id !== eventId) : [...prev, eventId]);
  };
  // Gather all free events with their source category info
  const allFreeEvents = useMemo(() => {
    const events: (SampleEvent & {
      categorySlug: string;
    })[] = [];
    Object.entries(EVENT_CATEGORIES).forEach(([slug, config]) => {
      config.sampleEvents.forEach(event => {
        if (isFreeEvent(event)) {
          events.push({
            ...event,
            categorySlug: slug
          });
        }
      });
    });
    return events;
  }, []);
  // Apply category filter
  const categoryFiltered = useMemo(() => {
    const filter = CATEGORY_FILTERS.find(f => f.label === activeFilter);
    if (!filter || filter.slugs === 'all') return allFreeEvents;
    return allFreeEvents.filter(e => (filter.slugs as string[]).includes(e.categorySlug));
  }, [allFreeEvents, activeFilter]);
  // Apply location search
  const filteredEvents = useMemo(() => {
    if (!locationSearch) return categoryFiltered;
    return categoryFiltered.filter(e => e.location.toLowerCase().includes(locationSearch.toLowerCase()));
  }, [categoryFiltered, locationSearch]);
  return <div className={`min-h-screen ${dk.page} py-8`} data-id="element-9158" data-mp="2842c35r0mpjm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-9159" data-mp="2839bynmkayag">
        {/* Header */}
        <div className="text-center mb-8" data-id="element-9160" data-mp="242yvgh158phc">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 mb-4" data-id="element-9161" data-mp="23zuojbwanfmz">
            <SparklesIcon className="h-6 w-6 text-green-600 dark:text-green-400" data-id="element-9162" data-mp="1u381qhkeqeib" />
          </div>
          <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-9163" data-mp="1p5q25hsa5bmz">
            Free Events
          </h1>
          <p className={`mt-2 text-lg ${dk.textSecondary}`} data-id="element-9164" data-mp="3oiao46e5tgh0">
            Discover free book events, meetups, and virtual gatherings
          </p>
        </div>

        {/* Category Filter Pills */}
        <div className="mb-6 -mx-4 px-4 overflow-x-auto" data-id="element-9165" data-mp="3rjdtw36wym2i">
          <div className="flex gap-2 min-w-max pb-2" data-id="element-9166" data-mp="3rhsyycgot144">
            {CATEGORY_FILTERS.map(filter => <button key={filter.label} onClick={() => setActiveFilter(filter.label)} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${activeFilter === filter.label ? 'bg-indigo-600 text-white dark:bg-indigo-500' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'}`} data-id="element-9167" data-mp="3hlhjxepppx5z">
                {filter.label}
              </button>)}
          </div>
        </div>

        {/* Search */}
        <SearchBar locationSearch={locationSearch} setLocationSearch={setLocationSearch} placeholder="Search free events by location..." data-id="element-9168" data-mp="33uulzors0bay" />

        {/* Results count */}
        <div className={`mb-6 text-sm font-medium ${dk.textMuted}`} data-id="element-9169" data-mp="2tvdgp8gfrzfi">
          Showing {filteredEvents.length} free event
          {filteredEvents.length !== 1 ? 's' : ''}
        </div>

        {/* Event Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-9170" data-mp="26aclwradi0ac">
          {filteredEvents.map(event => <EventCard key={`${event.categorySlug}-${event.id}`} title={event.title} date={event.date} time={event.time} type={event.type} description={event.description} image={event.image} location={event.location} eventId={event.id} isSaved={savedEvents.includes(event.id)} onToggleSave={handleToggleSave} status={event.status} price="Free" isVirtual={event.isVirtual} country={event.country} data-id="element-9171" data-mp="2k5wrkl13clzt">
              {event.author && <div className={`text-sm font-medium mb-2 ${dk.textPrimary}`} data-id="element-9172" data-mp="37z6ji5f33jlh">
                  by {event.author}
                </div>}
              <div className={`flex items-center text-xs ${dk.textMuted}`} data-id="element-9173" data-mp="29m0y6uqcutra">
                <UsersIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-9174" data-mp="29jy9qf4up8oo" />
                {event.attendees} attending •{' '}
                {event.maxAttendees - event.attendees} spots left
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800" data-id="element-9175" data-mp="2jd5fi13i6qpa">
                <span className={`inline-block bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 text-xs px-2.5 py-1 ${rt.badgeRadius} font-medium`} data-id="element-9176" data-mp="2oafdi54ykaoq">
                  Free Event
                </span>
              </div>
            </EventCard>)}
        </div>

        {/* Empty State */}
        {filteredEvents.length === 0 && <div className="text-center py-12" data-id="element-9177" data-mp="12z5h4ukb3945">
            <CalendarIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textMuted}`} data-id="element-9178" data-mp="17wfcsryagh0f" />
            <p className={`text-lg ${dk.textMuted}`} data-id="element-9179" data-mp="10do88jqig4uw">
              No free events found matching your filters.
            </p>
            <button onClick={() => {
          setActiveFilter('All');
          setLocationSearch('');
        }} className="mt-4 text-sm font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300" data-id="element-9180" data-mp="vlo229jukfpv">
              Clear all filters
            </button>
          </div>}
      </div>
    </div>;
};
export default FreeEvents;